import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { PulseemReactInstance } from '../../helpers/Api/PulseemReactAPI';
// READ-ONLY imports. `smartSendSlice` is byte-identical (contract §1 / 14-W3 §4): this slice
// listens to its action types, and importing the creators is how it does so without a
// hand-written string literal that can drift from the real `typePrefix` (`SmartSend/Send`,
// not `SmartSend/SendSmart`) and then silently never fire.
import { sendSmart, setCampaignContext } from './smartSendSlice';
import {
    WaAvailability, WaTemplateItem, WaMappingResult, WaSaveMappingRequest, WaSummaryResult,
    WaPreviewResult, WaTestSendResultItem, WaPendingLeg, WaSendOutcome, WaVariableMap, WaSaveState,
    isPreviewEmptyCode, SmartSendMappingSuggestion,
} from '../../Models/DataSources/SmartSendWhatsApp';
import { ChannelStateItem, ChannelSetItem } from '../../Models/DataSources/SmartSendChannels';

// SmartSend part B — the WhatsApp slice. Store key `smartSendWa` (CONTRACT §6).
//
// 🔴 WHY THIS IS A SEPARATE SLICE AND NOT A BRANCH OF smartSendSlice:
// `redux/reducers/smartSendSlice.ts` is on the contract's byte-identical list (§1, "React
// בייט-זהים"), and 14-W3 §4 forbids touching it. Everything this feature needs from it is
// obtained by LISTENING to its action types in extraReducers — never by editing it.
// ⚠️ 2026-09-23 · 08-mapping-memory: smartSendSlice.ts is no longer byte-identical (this note and
// the one at :3). Plan v2 §4.5 (Idan) lists ONE additive change there — the e-mail memory read,
// `getMappingSuggestions` and its four `memorySuggestions*` fields. Nothing this slice listens to
// (`setCampaignContext`, `sendSmart`) moved, and nothing in THIS slice edits it.
//
// House rules copied verbatim from smartSendSlice (dataSourcesSlice convention):
// return response.data (NO JSON.parse), catch → thunkAPI.rejectWithValue({ error: error.message }).
//
// There is deliberately NO mock switch here. 14-W3 §0 is explicit: the API lands in parallel
// (W2), the client is built against jest fixtures, and `USE_SEND_MOCK`-style guards must not be
// added — they are exactly what hid a 200-vs-201 mismatch in the e-mail pipeline.

const api = 'DataSourcesSender/';

type LoadStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

export interface WaTemplateCard {
    name: string | null;
    language: string | null;
    variables: number;
}

export interface WaSavedMapping {
    campaignId: number;
    templateId: string | null;
    mappedCount: number;
    placeholderCount: number;
}

export interface WaPreviewState {
    offset: number;
    row: WaPreviewResult | null;
    values: { [index: string]: string };
    // 🔴 WHICH OFFSET `row` ACTUALLY CAME FROM — not which one the stepper is displaying.
    // The response carries no echo of the request (`RowOrdinal` is the row's position in the
    // DATA SOURCE, not in the candidate set — that confusion was C7), so the only honest source
    // is the request itself: `action.meta.arg.offset`. Same pattern, same reason, as
    // `mappingCampaignId`.
    //
    // It exists so a consumer can ask "is the row on screen the row the label claims?" and get a
    // real answer. The test-send dialog gates a CHARGED send on it.
    rowOffset: number | null;
}

// ══ 09-channel-units — WHICH UNITS THE MAPPING SCREEN CARRIES ═══════════════════════════════════
// Every channel is its own unit: its own container and its own mapping row (06-docs §1.1). The
// mapping screen reads the set off its URL and nothing else:
//   'email'    — the page is an e-mail campaign, sent alone;
//   'both'     — the page is an e-mail campaign, sent together with the WhatsApp unit `?wa={id}`;
//   'whatsapp' — the page IS the WhatsApp unit (`?channel=3`).
// `null` everywhere else (the picker), where the intent flags below still speak for the operator.
export type WaUnitMode = 'email' | 'both' | 'whatsapp';

interface SmartSendWaState {
    campaignId: number | null;
    // 09-channel-units — set by the mapping screen from its URL, right after `setCampaignContext`.
    // `waUnitId` is the WhatsApp unit's OWN id (a WACampaignID), the one every WhatsApp request on
    // that screen is addressed to, always on channel 3. Null ⇒ the page carries no WhatsApp unit.
    unitMode: WaUnitMode | null;
    waUnitId: number | null;
    // The operator's WhatsApp tick. Raised by the picker tile; on the mapping screen written by
    // `setWaUnit` from the page's units (true exactly when the URL names a WhatsApp unit), which is
    // also what keeps the tabs after a refresh. No leg raises it any more (09-channel-units).
    withWhatsApp: boolean;
    // 🔴 THE LEG'S CHANNEL STATE, AND WHY IT IS A DIFFERENT AXIS FROM `selectedChannel`.
    //
    // ⚠️ CORRECTED 2026-09-22. The comment that stood here said WHATSAPP is declared
    // `enabled: false` in `Models/DataSources/SmartSend.ts`, and concluded that "WhatsApp only"
    // is NOT REPRESENTABLE in `selectedChannel`. Both halves are false against the current code:
    // the third `CHANNELS` entry carries `enabled: true`, and `selectedChannel` holds 3 on every
    // WhatsApp-only mapping screen. Measured, not inferred — the flag is on the descriptor row
    // itself, and `SmartSendScreen` writes 3 into the slice from `?channel=3`.
    //
    // The two pieces of state answer different questions, which is the real reason both exist:
    //   `selectedChannel`  WHICH CONTAINER this screen is addressing. 1 ⇒ dbo.Campaigns,
    //                      3 ⇒ dbo.WhatsAppCampaign. Derived per campaign from the URL in
    //                      SmartSendScreen.tsx:99-104, never from whatever was chosen last.
    //   `suppressEmail`    On a channel-1 LEG campaign (dbo.CampaignWhatsAppLeg), whether the
    //                      e-mail arm is switched off. It says nothing about which container
    //                      the mapping is written to — that row is always the e-mail campaign's.
    //
    // So the three operator-visible states below are LEG states, reached with `selectedChannel`
    // sitting at 1 throughout. A stand-alone container is the fourth state and does not appear
    // here at all: it carries `selectedChannel === 3` and has no leg row and no `suppressEmail`.
    //   no leg               => e-mail only
    //   suppressEmail=false  => both
    //   suppressEmail=true   => WhatsApp only, via the leg
    suppressEmail: boolean;
    // Whether the OPERATOR has moved the switch since the last time the server told us its
    // value. It is what decides whether `SuppressEmail` rides in the next POST body at all —
    // see `WaSaveMappingRequest.SuppressEmail`. Without it, a save issued before `GET Mapping`
    // had seeded `suppressEmail` would write `false` over a live `true` and mail the whole
    // audience; with it, that save simply omits the field and the server leaves the column
    // alone (`ISNULL(@prm_SuppressEmail, SuppressEmail)`, §3.1).
    suppressEmailDirty: boolean;
    whatsAppTemplateId: string | null;       // ContentSid — NOT the numeric Id (v1.5)
    whatsAppTemplateDbId: number | null;
    whatsAppFromNumber: string | null;
    waPlaceholderCount: number;
    waTemplateCard: WaTemplateCard | null;
    waVariableMap: WaVariableMap;
    waDirty: boolean;
    waSaveState: WaSaveState;
    waSavedMapping: WaSavedMapping | null;
    waAvailability: WaAvailability | null;
    availabilityStatus: LoadStatus;
    waTemplates: WaTemplateItem[];
    templatesStatus: LoadStatus;
    waMapping: WaMappingResult | null;
    // 🔴 C-40 — THE §4 `Message` OF A REFUSED `GET WhatsApp/Mapping`, or null.
    // `mappingStatus = 'failed'` was written on two paths and read by NOBODY, so a refusal
    // produced a screen with no WhatsApp on it and no reason given — the operator could not
    // tell a campaign without WhatsApp from one whose read had failed. The most common refusal
    // is not exotic: `409 WA_EMAIL_LEG_MISSING` for every campaign that has no
    // `CampaignsToDataSources` row yet, which is every campaign before its first save.
    mappingError: string | null;
    // 🔴 WHICH campaign `waMapping` describes. The picker calls `getWaMapping` per SELECTED
    // campaign and never dispatches `setCampaignContext`, so until the second call resolves the
    // slice still holds the FIRST campaign's leg. Without this field "Continue" would raise the
    // removal dialog from campaign A's leg while the selection is campaign B — and confirming it
    // would DELETE B's mapping. Same campaignId-equality guard `savedMapping` and `campaignName`
    // already use on the mapping screen, and for the same reason.
    mappingCampaignId: number | null;
    mappingStatus: LoadStatus;
    waSummary: WaSummaryResult | null;
    summaryStatus: LoadStatus;
    summaryError: string | null;             // the §4 `Message` of a 4xx, for t('error.<Message>')
    waPreview: WaPreviewState;
    previewStatus: LoadStatus;
    // The §4 `Message` of a failed `GET WhatsApp/Preview`. It exists because `previewStatus`
    // alone was written on two paths and read by nobody: a refused preview left the PREVIOUS
    // row on screen, unchanged and unmarked, on the surface the operator uses to decide what a
    // charged send will say.
    previewError: string | null;
    // The offset of a `GET Preview` currently on the wire, for de-duplication. Two components
    // legitimately want the same row at the same moment (the phone preview and the test dialog,
    // both self-sufficient by design); firing the same GET twice per stepper click is waste, not
    // safety.
    previewOffsetInFlight: number | null;
    // 🔴 WHICH request owns the preview state right now — RTK's own `meta.requestId`.
    //
    // `previewOffsetInFlight` alone is a dedupe KEY, not an identity, and it was not scoped to a
    // request: a reset (a template change) could leave it set, a second request could overwrite
    // it, and a LATE answer from the first could then clear the second's marker — or worse,
    // write its row and `rowOffset` over the newer one. `rowOffset` kept the SEND safe either
    // way, but the phone would be showing a row nobody asked for.
    //
    // With this, every terminal case ignores an action that is not the current request, and a
    // reset invalidates whatever is in flight by clearing the id.
    previewReqId: string | null;
    waSendOutcome: WaSendOutcome | null;
    waTestResult: WaTestSendResultItem[] | null;
    // 🔴 The test dialog's ONLY other outcome channel. `waTestResult` answers "what came back
    // per number"; every route-level refusal (400/404/409/422 and every MapWaReturnCode arm)
    // returns with no `Data` at all, and the dialog's non-empty gate then rendered NOTHING —
    // indistinguishable from "the click did nothing", on a dialog whose own banner says each
    // test message is a real charged send. That is an invitation to press it again.
    waTestError: string | null;
    waPendingLegs: WaPendingLeg[];
    pendingLegsStatus: LoadStatus;
    // The `Message` of the last failed save, so the autosave indicator can render
    // t('DataSources.send.whatsapp.error.' + lastSaveError) instead of a generic string.
    lastSaveError: string | null;

    // ══ 67 (5) · 70 (4.1) — THE CHANNEL ARRAY. ONE CALL, AND IT IS THE AUTHORITY. ════════
    // 🔴 This replaces deriving "which channels are on" from `waMapping.HasLeg` plus
    // `suppressEmail`. A leg survives a soft delete, so the leg stopped answering the
    // question (67 (3.3.0)) — and the same derivation was being made in five places.
    //
    // ⚠️ `null` MEANS "NOT READ YET", NOT "no channels". The two are different answers and
    // the selectors keep them apart: until this is loaded for THIS campaign the screen falls
    // back to the operator INTENT for display, and `selectChannelsReady` is false so the send
    // button waits. That split is the C-29 rule already in this package — display reads the
    // intent, sending reads the state — applied to the new source of the state.
    channels: ChannelStateItem[] | null;
    channelsStatus: LoadStatus;
    // Which campaign the array above belongs to. Without it a stale array from the previous
    // campaign answers for the current one during navigation - and "the WhatsApp channel is
    // on" is exactly the answer that must never be carried over.
    channelsCampaignId: number | null;
    channelsError: string | null;
    // 🔴 M10 · 2026-09-20 — THE WRITE'S OWN ERROR, SEPARATE FROM THE READ'S.
    // `channelsError` is written by BOTH thunks, so a banner on it would light up on a benign
    // GET failure at mount — when the selectors fall back and the screen still works. This one
    // is set only by POST, and it is what the screen renders.
    channelsSaveError: string | null;

    // ══ 08-mapping-memory — "USED LAST TIME", FOR THE TEMPLATE ON SCREEN ═════════════════════
    // The rows of `GET WhatsApp/MappingSuggestions/{id}`. A CONVENIENCE: a failed or absent read
    // leaves `[]` and the tab offers exactly what it offered before this feature existed.
    waMemorySuggestions: SmartSendMappingSuggestion[];
    // 🔴 WHICH request the rows ANSWER — `waMemorySuggestionsKey(meta.arg)`, stamped from the
    // REQUEST, because the response carries no echo of it (the same reason `rowOffset` and
    // `mappingCampaignId` are stamped from `meta.arg`). The tab shows the rows only while this
    // equals the key it would ask with NOW; any other rows answer a campaign, container channel,
    // version or template that is no longer the one on screen.
    waMemorySuggestionsKey: string | null;
    // RTK's `meta.requestId` of the request that owns the state — the `previewReqId` pattern. An
    // answer from a superseded request changes nothing.
    waMemorySuggestionsReqId: string | null;
    waMemorySuggestionsStatus: LoadStatus;
}


const emptyPreview: WaPreviewState = { offset: 0, row: null, values: {}, rowOffset: null };

const initialState: SmartSendWaState = {
    campaignId: null,
    unitMode: null,
    waUnitId: null,
    withWhatsApp: false,
    suppressEmail: false,
    suppressEmailDirty: false,
    whatsAppTemplateId: null,
    whatsAppTemplateDbId: null,
    whatsAppFromNumber: null,
    waPlaceholderCount: 0,
    waTemplateCard: null,
    waVariableMap: {},
    waDirty: false,
    waSaveState: 'idle',
    waSavedMapping: null,
    waAvailability: null,
    availabilityStatus: 'idle',
    waTemplates: [],
    templatesStatus: 'idle',
    waMapping: null,
    mappingError: null,
    mappingCampaignId: null,
    mappingStatus: 'idle',
    waSummary: null,
    summaryStatus: 'idle',
    summaryError: null,
    waPreview: emptyPreview,
    previewStatus: 'idle',
    previewError: null,
    previewOffsetInFlight: null,
    previewReqId: null,
    waSendOutcome: null,
    waTestResult: null,
    waTestError: null,
    waPendingLegs: [],
    pendingLegsStatus: 'idle',
    lastSaveError: null,
    channels: null,
    channelsStatus: 'idle',
    channelsCampaignId: null,
    channelsError: null,
    channelsSaveError: null,
    waMemorySuggestions: [],
    waMemorySuggestionsKey: null,
    waMemorySuggestionsReqId: null,
    waMemorySuggestionsStatus: 'idle',
};


// ── thunks — 1:1 with CONTRACT §5 ────────────────────────────────────────────────────────────

// ══ 67 (5) — GET / POST api/DataSourcesSender/Channels/{campaignId} ═══════════════════════
// 🔴 ONE call replaces the five private calculations of "which channels are on".
// The POST sends the FULL set and answers with the RECOMPUTED array (67 (5) rule 3), so there
// is no window in which the screen draws a state the server has already moved past.
export const getChannels = createAsyncThunk(
    'SmartSendWa/GetChannels', async (campaignId: number, thunkAPI) => {
        try {
            const response = await PulseemReactInstance.get(`${api}Channels/${campaignId}`);
            return { campaignId, data: response.data };
        } catch (error: any) {
            return thunkAPI.rejectWithValue({ error: error.message });
        }
    });

// ⚠️ THE BODY IS THE FULL SET. A channel left out of `channels` is switched OFF by the server.
// 🔴 M10 · 2026-09-20 — A REFUSED POST Channels PUTS THE OPERATOR'S INTENT BACK.
// The switch is flipped BEFORE the request (`SmartSendScreen.tsx:189`, then `:191`), so without
// this a refusal leaves the screen asserting a state the server never accepted — the exact
// screen/server divergence this wave exists to remove.
//
// THE PRE-CLICK VALUE IS IN THE REQUEST, so no snapshot field is needed. The card renders
// `on: !suppressEmail` (`SmartSendScreen.tsx:183`, read raw off the slice at `:161`), and the only
// two callers in the tree click `!emailToggle.on` (`ChannelSelector.tsx:229` and `:236`)
// ⇒ `next = !on_old = suppressEmail_old`, and `next` is what travels as `email.IsActive`.
//
// ⚠️ THE campaignId GATE IS NOT DECORATION. `setCampaignContext` (:521) re-keys the whole slice;
// a refusal landing after a navigation would write campaign A's e-mail intent onto campaign B.
// `getChannels.pending` already carries the same gate, for the same reason.
//
// ⚠️ AND `suppressEmailDirty` IS RAISED, NOT LEFT ALONE. `POST Mapping` is a SECOND writer of
// the e-mail channel bit — the bridge writes `dbo.CampaignChannel` Channel=1 itself
// (`68e_SetMapping_Bridge.sql:544-560`) — and its `fulfilled` clears the marker (:727). If the
// 750ms autosave (`useWaAutosave.ts:143-151`) fires before a slow refusal comes back, the server
// already holds the NEW value with the marker down, and a revert without raising it would leave
// screen=OLD against server=NEW: the same lie, inverted. Raising it arms the corrective save.
// In the ordinary path the marker is already true, so this is a no-op.
const revertEmailIntent = (
    state: { campaignId: number | null; suppressEmail: boolean; suppressEmailDirty: boolean },
    campaignId: number | null,
    channels: any,
): void => {
    if ((campaignId ?? null) !== (state.campaignId ?? null)) { return; }
    const email = Array.isArray(channels) ? channels.find((c: any) => c?.Key === 'email') : undefined;
    if (!email) { return; }
    state.suppressEmail = !!email.IsActive;
    state.suppressEmailDirty = true;
};

// Callers build the complete list; there is deliberately no "toggle one channel" thunk, because
// that shape is what makes a partial set look safe.
export const setChannels = createAsyncThunk(
    'SmartSendWa/SetChannels',
    async (arg: { campaignId: number; channels: ChannelSetItem[] }, thunkAPI) => {
        try {
            const response = await PulseemReactInstance.post(
                `${api}Channels/${arg.campaignId}`, { Channels: arg.channels },
            );
            return { campaignId: arg.campaignId, data: response.data };
        } catch (error: any) {
            return thunkAPI.rejectWithValue({ error: error.message });
        }
    });

export const getWaAvailability = createAsyncThunk(
    'SmartSendWa/Availability', async (_: void, thunkAPI) => {
        try {
            const response = await PulseemReactInstance.get(`${api}WhatsApp/Availability`);
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue({ error: error.message });
        }
    });

// ── POST api/DataSourcesSender/WhatsApp/Container ────────────────────────────────────────
// In e-mail the operator picks an existing campaign; in WhatsApp she picks a TEMPLATE and the
// campaign is created from it (owner, 21.09). This is that creation. It returns the container's
// WACampaignID, which is the id every later call on this channel is keyed on — the mapping row,
// the variable map and the send.
// 09-channel-units — `dataSourceId` makes the server create the unit WHOLE: the container AND its own
// mapping row (CampaignsToDataSources, Channel 3) in the same request, so the mapping screen opens
// on a unit that exists and nothing later has to bind it. `dataSourceVersionId` is optional; absent
// ⇒ the server locks the source's active version, and the mapping screen's save re-binds to the
// version on screen if they differ.
export const createWaContainer = createAsyncThunk(
    'SmartSendWa/CreateContainer',
    async (arg: {
        whatsAppTemplateID: string; fromNumber?: string | null; name?: string | null;
        dataSourceId?: number | null; dataSourceVersionId?: number | null;
        // 12-fix-wave 7.1 / 7.3 — the e-mail campaign of a "both" send. The server joins the unit to
        // that e-mail's row (contract 5.3 step 4). Sent only when > 0, like `DataSourceID`.
        campaignId?: number | null;
    }, thunkAPI) => {
        try {
            const response = await PulseemReactInstance.post(`${api}WhatsApp/Container`, {
                WhatsAppTemplateID: arg.whatsAppTemplateID,
                FromNumber: arg.fromNumber || null,
                Name: arg.name || null,
                ...(arg.campaignId != null && arg.campaignId > 0 ? { CampaignID: arg.campaignId } : {}),
                ...(arg.dataSourceId != null && arg.dataSourceId > 0 ? { DataSourceID: arg.dataSourceId } : {}),
                // A version only WITH its source (the server refuses a version on its own).
                ...(arg.dataSourceId != null && arg.dataSourceId > 0
                    && arg.dataSourceVersionId != null && arg.dataSourceVersionId > 0
                    ? { DataSourceVersionID: arg.dataSourceVersionId } : {}),
            });
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue({ error: error.message });
        }
    });

export const getWaTemplates = createAsyncThunk(
    'SmartSendWa/Templates', async (_: void, thunkAPI) => {
        try {
            const response = await PulseemReactInstance.get(`${api}WhatsApp/Templates`);
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue({ error: error.message });
        }
    });

// channel 3: `campaignId` IS the WhatsApp container's WACampaignID, not a dbo.Campaigns id.
// The parameter is what tells the server WHICH TABLE the number belongs to; without it the
// ownership gate asks dbo.Campaigns about an id from another table and answers 404.
export type WaMappingArg = number | { campaignId: number; channel?: number };

export const getWaMapping = createAsyncThunk(
    'SmartSendWa/GetMapping', async (arg: WaMappingArg, thunkAPI) => {
        try {
            const campaignId = typeof arg === 'number' ? arg : arg.campaignId;
            const channel = typeof arg === 'number' ? undefined : arg.channel;
            const q = channel && channel !== 1 ? `?channel=${channel}` : '';
            const response = await PulseemReactInstance.get(`${api}WhatsApp/Mapping/${campaignId}${q}`);
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue({ error: error.message });
        }
    });

// QA BUG-35 — THE SAME READ (GET WhatsApp/Mapping, channel 3), WRITING NOTHING. The picker asks what the WhatsApp unit
// already joined to an e-mail campaign's send is (its template, its number, how many variables are mapped) before it asks
// the operator what to do with it. It owns no unit, so the answer must not land in this slice: `getWaMapping.fulfilled`
// would seed `waMapping` (whose HasLeg the picker's intent selectors read as "WhatsApp on") and the template card.
// The caller reads `payload.Data` from the dispatch result, through `waUnitSummary`. No extraReducers case, on purpose.
export const peekWaMapping = createAsyncThunk(
    'SmartSendWa/PeekMapping', async (waCampaignId: number, thunkAPI) => {
        try {
            const response = await PulseemReactInstance.get(`${api}WhatsApp/Mapping/${waCampaignId}?channel=3`);
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue({ error: error.message });
        }
    });

export const setWaMapping = createAsyncThunk(
    'SmartSendWa/SetMapping',
    async (arg: { campaignId: number; body: WaSaveMappingRequest; channel?: number }, thunkAPI) => {
        try {
            const q = arg.channel && arg.channel !== 1 ? `?channel=${arg.channel}` : '';
            const response = await PulseemReactInstance.post(`${api}WhatsApp/Mapping/${arg.campaignId}${q}`, arg.body);
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue({ error: error.message });
        }
    });

// 🔴 v1.23d — `confirmSuppressedDelete`. On a leg carrying `SuppressEmail = 1` this route
// answers `409 WA_SUPPRESS_CONFIRM_REQUIRED` until the flag is present, because deleting that
// leg REMOVES THE ONLY THING SUPPRESSING THE E-MAIL — a direct call would switch the mail back
// on for the whole audience with nobody having chosen it.
//
// ⚠️ THE CLIENT DIALOG FILLS THIS PARAMETER; IT DOES NOT REPLACE IT. The two are not
// alternatives — §8's rule that a client-side guard does not satisfy an invariant is exactly
// why the server gate exists, and the operator's answer is exactly why the parameter does.
export type WaDeleteArg = number | { campaignId: number; confirmSuppressedDelete?: boolean };

// Exported so the one thing that matters about this route — whether the confirmation actually
// reaches the wire — can be asserted without a network. Same reason `shouldFetchPreview` is a
// named export: a rule that cannot be called without a store is a rule nobody tests.
//
// ⚠️ A bare number is still accepted. The e-mail-and-WhatsApp path has nothing to confirm, and
// forcing every caller into an object would have made the confirmed and unconfirmed calls look
// alike at the call site — which is the one place they must not.
export const waDeleteMappingPath = (arg: WaDeleteArg): string => {
    const campaignId = typeof arg === 'number' ? arg : arg.campaignId;
    const confirm = typeof arg === 'number' ? false : !!arg.confirmSuppressedDelete;
    return `${api}WhatsApp/Mapping/${campaignId}${confirm ? '?confirmSuppressedDelete=true' : ''}`;
};

export const deleteWaMapping = createAsyncThunk(
    'SmartSendWa/DeleteMapping', async (arg: WaDeleteArg, thunkAPI) => {
        try {
            const response = await PulseemReactInstance.delete(waDeleteMappingPath(arg));
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue({ error: error.message });
        }
    });

export type WaSummaryArg = number | { campaignId: number; channel?: number };

export const getWaSummary = createAsyncThunk(
    'SmartSendWa/Summary', async (arg: WaSummaryArg, thunkAPI) => {
        try {
            const campaignId = typeof arg === 'number' ? arg : arg.campaignId;
            const channel = typeof arg === 'number' ? undefined : arg.channel;
            const q = channel && channel !== 1 ? `?channel=${channel}` : '';
            const response = await PulseemReactInstance.post(`${api}WhatsApp/Summary/${campaignId}${q}`, {});
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue({ error: error.message });
        }
    });

export const getWaPreview = createAsyncThunk(
    'SmartSendWa/Preview',
    async (arg: { campaignId: number; rowId?: number | null; offset?: number | null; channel?: number }, thunkAPI) => {
        try {
            const qs = new URLSearchParams();
            if (arg.rowId != null) qs.set('rowId', String(arg.rowId));
            if (arg.offset != null) qs.set('offset', String(arg.offset));
            // channel 3 = campaignId is a WACampaignID. Omitted for 1 so the URL is byte-identical
            // to today's on every existing call.
            if (arg.channel && arg.channel !== 1) qs.set('channel', String(arg.channel));
            const q = qs.toString();
            const response = await PulseemReactInstance.get(
                `${api}WhatsApp/Preview/${arg.campaignId}${q ? `?${q}` : ''}`);
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue({ error: error.message });
        }
    },
    { condition: (arg: any, api: any) => shouldFetchPreview(arg, api.getState()?.smartSendWa) });

// 🔴 THE DE-DUPLICATION RULE, as a named pure function — exported so it can be TESTED.
//
// Two components fetch the preview independently and on purpose (the phone preview and the test
// dialog; the dialog borrowing the sibling's effect is what let a charged send use a row the
// label did not claim). When both want the SAME offset at the SAME moment, one request is
// enough, and `condition` is redux-toolkit's own mechanism for saying so.
//
// ⚠️ IT IS EXTRACTED BECAUSE THE ALTERNATIVE IS UNTESTABLE. In RTK 1.7.2 a `condition` that
// returns false is NOT observable from a reducer: `createAsyncThunk` computes
//   `skipDispatch = options && !options.dispatchConditionRejection
//                && rejected.match(finalAction) && finalAction.meta.condition`
// and then `if (!skipDispatch) dispatch(finalAction)` — so the rejection never reaches the
// store at all. An earlier version of this file carried a `.rejected` guard for
// `meta.condition` and a test that fed such an action by hand: dead code, and a test pinning an
// action the runtime cannot emit. Both are gone; this function is what gets tested instead.
export const shouldFetchPreview = (
    arg: { offset?: number | null; rowId?: number | null } | null | undefined,
    waState: { previewOffsetInFlight?: number | null } | null | undefined,
): boolean => (
    // ⚠️ The in-flight marker records the OFFSET only, while the thunk also accepts `rowId` and
    // builds it into the URL. A request that names a specific row is therefore never "the same
    // request" as one that does not, and de-duplicating it against the offset would drop a fetch
    // for a completely different row. No caller passes `rowId` today; this is one line so that
    // the first one who does is not the person who finds out.
    (arg && arg.rowId != null)
        ? true
        : !(waState && waState.previewOffsetInFlight === ((arg && arg.offset) ?? 0))
);

export const sendWaTest = createAsyncThunk(
    'SmartSendWa/TestSend',
    async (arg: { campaignId: number; rowId: number; phones: string[]; channel?: number }, thunkAPI) => {
        try {
            const response = await PulseemReactInstance.put(
                `${api}WhatsApp/TestSend/${arg.campaignId}${arg.channel && arg.channel !== 1 ? `?channel=${arg.channel}` : ''}`,
                { RowID: arg.rowId, PhoneNumbers: arg.phones });
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue({ error: error.message });
        }
    });

// 🔴🔴 77 §1 — THE CHANNEL THAT SENDS ON ITS OWN, AND ITS OWN ENDPOINT.
//
// Not to be confused with `sendWaRetry` above. That one is the LEG: its id is a CampaignID of
// dbo.Campaigns, the leg hangs off that campaign, and the route it calls proves ownership
// against dbo.Campaigns and refuses without a row there. It serves the combined product and the
// manual retry, and it is unchanged.
//
// THIS one's id is a WACampaignID — the key of dbo.WhatsAppCampaign, channel 3's own container.
// There is no e-mail campaign anywhere in the request: ownership is proved against that table
// (op-type 11, deployed by 78d) and _Send_Stage reads its mapping row on Channel 3 and its
// template from the container (78f). A campaign that never had an e-mail side can send.
//
// ⚠️ A SEPARATE THUNK RATHER THAN A FLAG ON THE OLD ONE, for the same reason the server kept the
//    routes apart: the two take ids from DIFFERENT TABLES whose IDENTITY ranges overlap by
//    design. One function taking "an id, plus a boolean saying which kind" is precisely the
//    ambiguity 77 §7 forbids — and the kind of parameter that gets passed wrong once and sends
//    somebody else's audience.
// 2026-09-30 — also takes { campaignId, sendToSupervisor } (argCampaignId below reads both shapes);
// the tick travels as ?sendToSupervisor=true, like PUT Send.
export const sendWhatsAppOnly = createAsyncThunk(
    'SmartSendWa/SendWhatsAppOnly', async (arg: number | { campaignId: number; sendToSupervisor?: boolean }, thunkAPI) => {
        const waCampaignId = typeof arg === 'number' ? arg : arg.campaignId;
        const toSupervisor = typeof arg === 'number' ? false : !!arg.sendToSupervisor;
        try {
            const response = await PulseemReactInstance.put(
                `${api}WhatsAppOnly/Send/${waCampaignId}${toSupervisor ? '?sendToSupervisor=true' : ''}`, {});
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue({ error: error.message });
        }
    });

// ══ 08-mapping-memory — GET api/DataSourcesSender/WhatsApp/MappingSuggestions/{campaignId} ══
// `?channel=&dataSourceVersionId=&templateId=` (plan v2 §4.4; Idan E1, 2026-09-23). Read-only
// metadata: column NAMES, never a recipient value. BOTH memory keys travel in the request and the
// server resolves no template of its own: it reads `SmartSendMappingMemory_Get` with the JWT's
// sub-account, memory channel 3, `templateId` and `dataSourceVersionId`, and that proc verifies
// the version belongs to the sub-account.
export interface WaMemorySuggestionsArg {
    campaignId: number;
    // The CONTAINER channel, exactly as every neighbouring WhatsApp route takes it: 1 = the e-mail
    // campaign and its WhatsApp leg, 3 = a stand-alone WhatsApp container (then `campaignId` is
    // the WACampaignID). NOT the memory channel — the server always reads memory channel 3 here.
    channel: number;
    // The version whose columns are ON SCREEN — the server resolves each remembered header in it.
    // `smartSend.lockedVersionId`: the value the e-mail save posts as `DataSourceVersionID` (E2).
    dataSourceVersionId: number;
    // The ContentSid of the template ON SCREEN — `smartSendWa.whatsAppTemplateId`, the value
    // `useWaAutosave` saves as `WhatsAppTemplateID` — SENT, as-is (E1). It only selects which of
    // the caller's OWN memory rows are read; those are filtered by the JWT's sub-account, never by
    // anything in the request. Contract per E1: blank, or over nvarchar(50), is 400 DATA_INCORRECT.
    templateId: string;
}

// THE one definition of "which request do these rows answer" — the reducer stamps it from
// `meta.arg` and the tab builds the key it would ask with now through the same function, so the
// two can never be computed two ways.
export const waMemorySuggestionsKey = (a: WaMemorySuggestionsArg): string =>
    `${a.campaignId}|${a.channel}|${a.dataSourceVersionId}|${a.templateId}`;

// No mock switch (14-W3 §0), same shape as every thunk above. All three parameters are ALWAYS on
// the URL — the channel included: this route is new, so there is no pre-existing URL to keep
// byte-identical by omitting it. `URLSearchParams` encodes the ContentSid, whatever it holds.
export const getWaMemorySuggestions = createAsyncThunk(
    'SmartSendWa/MappingSuggestions', async (arg: WaMemorySuggestionsArg, thunkAPI) => {
        try {
            const qs = new URLSearchParams();
            qs.set('channel', String(arg.channel));
            qs.set('dataSourceVersionId', String(arg.dataSourceVersionId));
            qs.set('templateId', arg.templateId);
            const response = await PulseemReactInstance.get(
                `${api}WhatsApp/MappingSuggestions/${arg.campaignId}?${qs.toString()}`);
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue({ error: error.message });
        }
    });

// ── helpers ──────────────────────────────────────────────────────────────────────────────────

const ok = (p: any) => !!p && (p.StatusCode === 200 || p.StatusCode === 201);

// 🔴 `getWaMapping` accepts a number OR { campaignId, channel }. `mappingCampaignId` is
// compared with `===` against a plain number in four places (the picker's tile guard, the
// template picker's leg guard, the pending-request guard below), so storing the OBJECT there
// makes every one of those comparisons false forever — the combined product's guards included.
// This is the ONE line that has to normalise; everything downstream keeps seeing a number.
const argCampaignId = (a: any): number | null =>
    (typeof a === 'number' ? a : (a && typeof a.campaignId === 'number' ? a.campaignId : null));

// 09-channel-units — A REQUEST ABOUT A UNIT THIS PAGE DOES NOT CARRY. Every WhatsApp request is a
// unit's own (channel 3, by its id); its pending state or its answer can outlive the page that sent
// it (the page was left, or moved to another unit, while it was in flight) and must then not touch
// the unit that is on screen now — nor the picker, which carries none.
const forOtherUnit = (state: any, action: any): boolean =>
    state.waUnitId == null || argCampaignId(action.meta?.arg) !== state.waUnitId;

// Seed the working map from the server's RS2. A row whose header is no longer in the locked
// version arrives with `ColumnInVersion=false`; its columnId is dropped so `selectWaUnmapped`
// counts it as unmapped (contract §6, v1.5) instead of silently sending a dead ColumnID.
const mapFromServer = (rows: WaMappingResult['Map'] | undefined): WaVariableMap => {
    const m: WaVariableMap = {};
    (rows ?? []).forEach((r) => {
        const inVersion = r.ColumnInVersion !== false;
        m[r.VariableIndex] = {
            columnId: inVersion && r.DataSourceColumnID != null ? r.DataSourceColumnID : null,
            staticValue: r.StaticValue ?? null,
        };
    });
    return m;
};

const countMapped = (m: WaVariableMap, placeholders: number): number => {
    let n = 0;
    for (let i = 1; i <= placeholders; i += 1) {
        const b = m[i];
        if (b && ((b.columnId != null && b.columnId > 0) || (b.staticValue != null && b.staticValue !== ''))) n += 1;
    }
    return n;
};

// QA BUG-35 — what a `peekWaMapping` answer says about the unit, in the words `getWaMapping.fulfilled` uses for the same
// answer (the card's name, `waSavedMapping.mappedCount` / `placeholderCount`), so the picker's question and the mapping
// page never count differently. null when the answer is not a unit's (no HasLeg).
export interface WaUnitSummary {
    templateId: string | null;
    templateName: string | null;
    fromNumber: string | null;
    mapped: number;
    total: number;
}
export const waUnitSummary = (d: WaMappingResult | null | undefined): WaUnitSummary | null => {
    if (!d || !d.HasLeg) return null;
    const total = d.PlaceholderCount ?? 0;
    return {
        templateId: d.WhatsAppTemplateID ?? null,
        templateName: d.Template ? (d.Template.FriendlyTemplateName || d.Template.TemplateName || null) : null,
        fromNumber: d.FromNumber ?? null,
        mapped: countMapped(mapFromServer(d.Map), total),
        total,
    };
};

// 🔴 2026-09-23 — RESOLVE A SEEDED TEMPLATE ID AGAINST THE LOADED LIST.
// `?waTemplate=` carries an id and nothing else, so `seedWaFromUrl` could set
// `whatsAppTemplateId` but not `waPlaceholderCount` — and the mapping table derives its rows
// from the COUNT alone (`waTokens.waVariables(placeholderCount)`), so every first visit rendered
// "לא נמצאו שדות בתבנית" over a template that has five. The server cannot supply the count
// either: `DataSources_WhatsApp_GetMapping_Stage:239` returns `PlaceholderCount` only when
// `@HasLeg = 1`, and the leg is what the operator is on this screen to create.
//
// The count is already in `waTemplates` — the same array, from the same thunk, that the picker
// card reads its "5 משתנים" from. This only closes the gap between the two readers.
//
// ⚠️ It deliberately does NOT touch `waVariableMap`. `selectWaTemplate` clears the map because a
// DIFFERENT template invalidates the bindings; this is the SAME template being described for the
// first time, and a map loaded by `getWaMapping.fulfilled` must survive it.
const applyTemplateShape = (state: any, templateId: string | null): void => {
    if (!templateId) return;
    const list: WaTemplateItem[] = Array.isArray(state.waTemplates) ? state.waTemplates : [];
    const tpl = list.find((t: any) => t && t.TemplateId === templateId);
    if (!tpl) return;
    state.whatsAppTemplateDbId = tpl.Id ?? state.whatsAppTemplateDbId;
    state.waPlaceholderCount = tpl.PlaceholderCount;
    state.waTemplateCard = {
        name: tpl.FriendlyTemplateName || tpl.TemplateName,
        language: tpl.Language,
        variables: tpl.PlaceholderCount,
    };
};

// 09-channel-units — EVERYTHING THAT BELONGS TO ONE WHATSAPP UNIT, and nothing that belongs to the
// operator's choice (tick, template, number, e-mail switch) or to the sub-account (Availability,
// template list). Reset by `setWaUnit` whenever the page's unit changes.
const resetUnitScope = (state: any): void => {
    state.waMapping = initialState.waMapping;
    state.mappingCampaignId = initialState.mappingCampaignId;
    state.mappingStatus = initialState.mappingStatus;
    state.mappingError = initialState.mappingError;
    state.waVariableMap = initialState.waVariableMap;
    state.waDirty = initialState.waDirty;
    state.waSaveState = initialState.waSaveState;
    state.waSavedMapping = initialState.waSavedMapping;
    state.lastSaveError = initialState.lastSaveError;
    state.waSummary = initialState.waSummary;
    state.summaryStatus = initialState.summaryStatus;
    state.summaryError = initialState.summaryError;
    state.waPreview = initialState.waPreview;
    state.previewStatus = initialState.previewStatus;
    state.previewError = initialState.previewError;
    state.previewOffsetInFlight = initialState.previewOffsetInFlight;
    state.previewReqId = initialState.previewReqId;
    state.waSendOutcome = initialState.waSendOutcome;
    state.waTestResult = initialState.waTestResult;
    state.waTestError = initialState.waTestError;
    state.waMemorySuggestions = initialState.waMemorySuggestions;
    state.waMemorySuggestionsKey = initialState.waMemorySuggestionsKey;
    state.waMemorySuggestionsReqId = initialState.waMemorySuggestionsReqId;
    state.waMemorySuggestionsStatus = initialState.waMemorySuggestionsStatus;
};

// ── slice ────────────────────────────────────────────────────────────────────────────────────

export const smartSendWhatsAppSlice = createSlice({
    name: 'smartSendWa',
    initialState,
    reducers: {
        // The picker tile. Turning it OFF here only clears the client's intent; it deletes
        // nothing on the server. 09-channel-units: removing the WhatsApp from a "both" send is the
        // tile on the mapping screen, which deletes the WhatsApp UNIT (DELETE DeleteMapping/{unit}
        // ?channel=3) only on confirm (SmartSendScreen `confirmRemoveWaUnit`).
        setWithWhatsApp: (state, action) => {
            state.withWhatsApp = !!action.payload;
            if (!action.payload) {
                state.whatsAppTemplateId = null;
                state.whatsAppTemplateDbId = null;
                state.waTemplateCard = null;
                state.waPlaceholderCount = 0;
                // 🔴 "BOTH CHANNELS OFF" IS NOT REPRESENTABLE, and this is the half of that
                // rule that lives in the state rather than in the UI (§6: "מצב «שני הערוצים
                // כבויים» אינו נגיש"). The picker refuses the click that would produce it, but
                // a state that can be REACHED another way is a state that eventually is: turn
                // WhatsApp off while the e-mail is suppressed and the campaign would have no
                // channel at all — and, worse, `suppressEmail` would still be true the next
                // time WhatsApp came back on. Turning WhatsApp off restores the e-mail, always.
                state.suppressEmail = false;
                state.suppressEmailDirty = true;
            }
        },
        // 🔴 v1.23 — the channel row's e-mail switch. It records INTENT; nothing is persisted
        // until the next mapping save carries it (the leg is the only home for this bit).
        // `dirty` is raised even when the value is unchanged, because the caller only reaches
        // here by an explicit operator action and "I meant this" is exactly what the POST needs
        // to be allowed to say.
        // 🔴 C2 · 2026-09-20 — PICKING A DIFFERENT CAMPAIGN STARTS FROM ITS OWN ANSWER.
        // The MAPPING SCREEN already gets this free: `setCampaignContext` (:580) does
        // `Object.assign(state, initialState, …)` whenever the id changes. THE PICKER DOES NOT
        // DISPATCH IT (see the note at :108), and `getWaMapping.fulfilled` cannot stand in for
        // it because every branch there is ONE-WAY: `if (!state.suppressEmailDirty)` skips the
        // e-mail bit once the operator has touched the switch, `if (d.HasLeg)` only ever turns
        // WhatsApp ON, and template / number / card fall back to `?? state.<previous>`.
        // ⇒ campaign A's "WhatsApp only", its template and its sender number rode into B, and
        // `SmartSendPicker` then put A's decision into B's URL as `?waOnly=1`.
        //
        // ⚠️ SELF-GUARDED on the id, so re-clicking the SAME row does not discard a tick the
        // operator just made. And deliberately NOT `Object.assign(…, initialState)`: on the
        // picker that would also wipe `waAvailability` and `waTemplates`, which are per-ACCOUNT
        // and were fetched once — re-fetching them on every row click is a round trip per click.
        // ⚠️ AND NOT `channels`: it carries its own campaign id and every consumer goes through
        // `selectChannelsReady`, which demands the id match, so a stale array cannot be read.
        // 🔴 2026-09-23 — NARROWED. It used to clear the operator's MESSAGE CHOICE as well, and
        // that is not campaign state. Reported from the stage test: picking a different e-mail
        // campaign wiped the WhatsApp tick and the chosen template, every time.
        //
        // Two kinds of state were being cleared by one action:
        //   · CAMPAIGN state — whether THIS campaign's e-mail is suppressed, its saved mapping,
        //     the dirty/save flags. Carrying campaign A's to B is wrong, and `suppressEmail` is
        //     the money-relevant one: it is what puts `?waOnly=1` in B's URL. Still cleared.
        //   · MESSAGE state — "also send WhatsApp", which template, from which number. None of
        //     that is a property of the e-mail campaign; it is what the operator decided to send.
        //     Changing WHICH campaign the source goes to does not un-choose the template.
        //     No longer cleared.
        //
        // 🔵 THE SERVER STILL WINS WHERE IT SHOULD. If the newly picked campaign already has a
        // leg, `getWaMapping.fulfilled` overwrites the template with ITS one (`:851`
        // `d.WhatsAppTemplateID ?? state.whatsAppTemplateId`) and applies its stored
        // `SuppressEmail` — the latter gated on `!state.suppressEmailDirty` (`:846`), which this
        // action still resets. So "server > URL > slice" is intact and the original C2 hazard
        // (A's `?waOnly=1` riding into B) stays closed; only the operator's own work survives.
        resetWaIntentForCampaign: (state, action) => {
            const next = action.payload ?? null;
            if (state.mappingCampaignId === next) { return; }
            state.suppressEmail = false;
            state.suppressEmailDirty = false;
            // The bindings are (template index → source column) and belong to the LEG being
            // edited, not to the template. On the picker this is empty anyway — mapping happens
            // on the next screen — so clearing it costs nothing and keeps B from inheriting A's.
            state.waVariableMap = {};
            state.waSavedMapping = null;
            state.waDirty = false;
            state.waSaveState = 'idle';
        },
        // QA BUG-31 — ENTERING THE PICKER STARTS CLEAN (Idan: so that no WhatsApp send is created by
        // mistake). The MESSAGE state that `resetWaIntentForCampaign` deliberately keeps across a
        // campaign change INSIDE the picker (the tick, the template, the number) is a leftover of the
        // previous visit when the picker is ENTERED, and it pre-ticked WhatsApp with the last template
        // on a brand-new campaign. Dispatched by SmartSendPicker with `resetForPicker` (mount and a
        // change of its URL parameters), not on a campaign click. The campaign state goes too, as in
        // `resetWaIntentForCampaign`. Per-account reads (`waAvailability`, `waTemplates`), the unit
        // (`setWaUnit`) and the mapping read stay.
        resetWaForPicker: (state) => {
            state.withWhatsApp = false;
            state.whatsAppTemplateId = null;
            state.whatsAppTemplateDbId = null;
            state.waTemplateCard = null;
            state.waPlaceholderCount = 0;
            state.whatsAppFromNumber = null;
            state.suppressEmail = false;
            state.suppressEmailDirty = false;
            state.waVariableMap = {};
            state.waSavedMapping = null;
            state.waDirty = false;
            state.waSaveState = 'idle';
            // A preview of the old template, or one still on the wire, describes nothing on screen.
            state.waPreview = emptyPreview;
            state.previewReqId = null;
            state.previewOffsetInFlight = null;
            state.previewStatus = 'idle';
            state.previewError = null;
        },
        setSuppressEmail: (state, action) => {
            state.suppressEmail = !!action.payload;
            state.suppressEmailDirty = true;
        },
        selectWaTemplate: (state, action) => {
            const tpl: WaTemplateItem | null = action.payload ?? null;
            state.whatsAppTemplateId = tpl ? tpl.TemplateId : null;
            state.whatsAppTemplateDbId = tpl ? tpl.Id : null;
            state.waPlaceholderCount = tpl ? tpl.PlaceholderCount : 0;
            state.waTemplateCard = tpl
                ? { name: tpl.FriendlyTemplateName || tpl.TemplateName, language: tpl.Language, variables: tpl.PlaceholderCount }
                : null;
            // A different template invalidates the working map: index 2 of template A is not
            // index 2 of template B, and carrying the bindings over would send B's recipients
            // A's values without a single visible change on screen.
            state.waVariableMap = {};
            state.waPreview = emptyPreview;
            // A preview already on the wire belongs to the OLD template. Dropping the request id
            // makes its answer arrive as a stale action that every terminal case ignores.
            state.previewReqId = null;
            state.previewOffsetInFlight = null;
            state.previewStatus = 'idle';
            state.previewError = null;
        },
        setWaFromNumber: (state, action) => { state.whatsAppFromNumber = action.payload ?? null; },
        // ONE write point for the variable map, mirroring smartSendSlice.setTokenMapping. Both
        // fields are written together so "column" and "static value" can never both be set.
        // QA BUG-08 (W-06) — `''` is KEPT: it is "«ערך קבוע» chosen, nothing typed yet", the state
        // that keeps the row's Select on the constant and its text field open (TokenMappingTable
        // `onExtra` is `staticValue != null`). Folding it to `null` closed the field the moment the
        // option was picked. `''` is still unbound for every reader (`isBound`, `countMapped`,
        // `unboundIndexes`, `selectWaUnmapped`), so it is never sent and never counts as mapped.
        setWaVariable: (state, action) => {
            const { variableIndex, columnId, staticValue } = action.payload ?? {};
            state.waVariableMap[variableIndex] = {
                columnId: columnId != null && columnId > 0 ? columnId : null,
                staticValue: staticValue != null ? staticValue : null,
            };
        },
        // `waDirty` is raised ONLY from user events — never from GET Mapping or from the URL
        // (contract §6 / 14-W3 §1). markWaEdited is the single choke point, exactly like
        // SmartSendScreen.markEdited, so a lock can disable editing by disabling one function.
        markWaEdited: (state) => { state.waDirty = true; },
        setWaSaveState: (state, action) => {
            state.waSaveState = action.payload?.state ?? 'idle';
            state.lastSaveError = action.payload?.message ?? null;
        },
        setWaPreviewOffset: (state, action) => { state.waPreview.offset = Number(action.payload) || 0; },
        clearWaSendOutcome: (state) => { state.waSendOutcome = null; },
        // BOTH channels. The dialog has two — the per-number list and the refusal banner — and
        // clearing only the list left a red "it failed" from the PREVIOUS run standing over a
        // freshly opened dialog whose own banner says every test message is charged. That is an
        // invitation to press send again.
        clearWaTestResult: (state) => { state.waTestResult = null; state.waTestError = null; },
        clearWaSummaryError: (state) => { state.summaryError = null; },
        // 12-fix-wave 7.4(a) — 409 SEND_USES_UNIT means the page asked about the send through the
        // e-mail id while its WhatsApp is a unit. The screen reloads the mapping instead of showing
        // an error, so the code is taken off every error surface it may have landed on.
        clearWaUsesUnitErrors: (state) => {
            const code = 'SEND_USES_UNIT';
            if (state.mappingError === code) { state.mappingError = null; }
            if (state.lastSaveError === code) { state.lastSaveError = null; state.waSaveState = 'idle'; }
            if (state.summaryError === code) { state.summaryError = null; }
            if (state.previewError === code) { state.previewError = null; }
            if (state.waTestError === code) { state.waTestError = null; }
        },
        // Seeded from `?waTemplate=` / `?waFrom=` on the mapping screen. Order of truth is
        // server > URL > slice, so this NEVER overwrites a value that came from a live leg —
        // the caller applies it only when the server has no leg.
        seedWaFromUrl: (state, action) => {
            const { templateId, fromNumber } = action.payload ?? {};
            if (templateId) {
                state.withWhatsApp = true;
                state.whatsAppTemplateId = templateId;
                // Covers the order "templates already loaded, then seed". The other order is
                // covered in `getWaTemplates.fulfilled`; between them the count is set whichever
                // of the two lands second.
                applyTemplateShape(state, templateId);
            }
            if (fromNumber) state.whatsAppFromNumber = fromNumber;
        },
        // 09-channel-units — the mapping screen's units, read off its URL. Dispatched right AFTER
        // `setCampaignContext`, which re-keys and wipes this slice on a new id, so it is never erased
        // by that reset. A unit id without a WhatsApp mode is not a unit and is dropped, and so is
        // anything that is not a positive int32 (a WACampaignID is an int IDENTITY).
        //
        // `unitMode: null` IS "NO MAPPING SCREEN": the picker dispatches it on arrival and the screen
        // on unmount. The units a page carried, and the unit's own server state, must not outlive
        // the page — a WhatsApp unit's mapping reports HasLeg = 1, which the picker's intent
        // selectors read as "WhatsApp is on". The operator's own choices (the tick, the template, the
        // number, the e-mail switch) stay: going back to the picker is going back to them.
        setWaUnit: (state, action) => {
            const mode: WaUnitMode | null = action.payload?.unitMode ?? null;
            const id = Number(action.payload?.unitId);
            const nextUnitId = (mode === 'both' || mode === 'whatsapp')
                && Number.isInteger(id) && id > 0 && id <= 2147483647 ? id : null;
            // WHATEVER BELONGED TO ANOTHER UNIT (or to none) GOES: its server state, its working map
            // and dirty flag, its save/send/summary/preview/test/memory results. `setCampaignContext`
            // only wipes on a new PAGE id, and two pages can share one — the "both" page and the
            // e-mail page of the same campaign — so without this a map left unsaved on one unit
            // would be posted by the next unit's autosave, and a dirty flag nobody can clear would
            // hold every exit dialog open. The operator's choices (tick, template, number, e-mail
            // switch) are not the unit's and stay.
            if (nextUnitId !== state.waUnitId) resetUnitScope(state);
            state.unitMode = mode;
            state.waUnitId = nextUnitId;
            if (mode == null) return;
            // THE PAGE'S UNITS ARE ITS CHANNEL CHOICE, and the intent fields say so from here on:
            // a page that carries a WhatsApp unit HAS WhatsApp (the tile and the tab do not wait for
            // a `?tab=whatsapp` to say so), and only the WhatsApp unit's own page has no e-mail.
            // Written here rather than taken from the unit's GET: on channel 3 GetMapping always
            // answers SuppressEmail = 1 (the container has no e-mail), which on a "both" page would
            // be the opposite of the truth — and would ride back into the picker as "e-mail off".
            state.withWhatsApp = state.waUnitId != null;
            state.suppressEmail = mode === 'whatsapp';
            state.suppressEmailDirty = false;
        },
    },
    extraReducers: (builder) => {
        builder
            // ── listening to smartSendSlice WITHOUT importing or editing it ──────────────────
            // The action TYPE string is the contract between the two slices. `setCampaignContext`
            // fires on every mount of SmartSendScreen; a different campaignId means this slice is
            // holding another campaign's leg, template and map, and none of it may survive.
            .addCase(setCampaignContext, (state, action: any) => {
                const next = action.payload?.campaignId ?? null;
                if (state.campaignId !== next) {
                    Object.assign(state, initialState, { campaignId: next });
                }
            })
            // The e-mail send result carries the WhatsApp outcome. 09-channel-units: `Data.WhatsApp`
            // is attached only when the send carried a WhatsApp unit (`PUT Send/{c}?waCampaignId=`,
            // the controller's Data composition is gated on `waUnit`); an e-mail-only send has none,
            // and the outcome is then null. Read from the EXISTING thunk.
            .addCase(sendSmart.fulfilled, (state, action: any) => {
                state.waSendOutcome = action.payload?.Data?.WhatsApp ?? null;
            })

            // ══ 67 (5) · 70 (4.1) — THE CHANNEL ARRAY ════════════════════════════════════
            // 🔴 `channels` IS ONLY EVER SET FROM A 200. A refusal leaves the PREVIOUS array
            // in place and raises `channelsStatus = failed`; it never writes `[]`. An empty
            // array is a real answer ("no rows ⇒ ordinary e-mail campaign") and a failed read
            // must not be able to forge it — that is law B (67 (3.4b)) on this side of the wire.
            .addCase(getChannels.pending, (state, action: any) => {
                state.channelsStatus = 'loading';
                state.channelsError = null;
                // The id is stamped on PENDING so a late answer for the previous campaign
                // cannot be mistaken for this one's.
                if (state.channelsCampaignId !== (action.meta?.arg ?? null)) {
                    state.channels = null;
                }
            })
            .addCase(getChannels.fulfilled, (state, action: any) => {
                if (!ok(action.payload?.data)) {
                    state.channelsStatus = 'failed';
                    state.channelsError = action.payload?.data?.Message ?? 'WA_INTERNAL';
                    return;
                }
                state.channels = action.payload?.data?.Data ?? null;
                state.channelsCampaignId = action.payload?.campaignId ?? null;
                state.channelsStatus = 'succeeded';
                state.channelsError = null;
            })
            .addCase(getChannels.rejected, (state, action: any) => {
                state.channelsStatus = 'failed';
                state.channelsError = action.payload?.error ?? 'WA_INTERNAL';
            })

            // The POST answers with the RECOMPUTED array (67 (5) rule 3), so the same handler
            // shape applies and no second GET is needed.
            // 🔴 M10 — AND NOTE THERE IS NO `pending` CASE, ON PURPOSE. Setting
            // `channelsStatus = 'loading'` here would drop `selectChannelsReady`
            // (`selectors.ts:48-51`) for the whole flight, and every consumer of
            // `selectServerChannelActive` — `selectWithWhatsApp` · `selectWaOnly` ·
            // `selectEmailOn` · `selectActiveChannels` · `waChannelOnServer` — would fall back
            // to the UNAPPROVED intent mid-write. `selectWaOnly` is the SEND route. A flight
            // flag must never be the readiness flag.
            //
            // 🔴 THE REFUSAL ARRIVES HERE, NOT IN `rejected`. This controller answers
            // `PulseemResponse`: 409 CHANNEL_NONE_ACTIVE and 503 WA_CONFIG_UNREADABLE sit in the
            // BODY of an HTTP 200, so `!ok(...)` is the real failure path and `rejected` is only
            // the transport one. Both revert.
            .addCase(setChannels.fulfilled, (state, action: any) => {
                if (!ok(action.payload?.data)) {
                    revertEmailIntent(state, action.meta?.arg?.campaignId ?? null, action.meta?.arg?.channels);
                    state.channelsStatus = 'failed';
                    state.channelsError = action.payload?.data?.Message ?? 'WA_INTERNAL';
                    state.channelsSaveError = action.payload?.data?.Message ?? 'WA_INTERNAL';
                    return;
                }
                state.channels = action.payload?.data?.Data ?? null;
                state.channelsCampaignId = action.payload?.campaignId ?? null;
                state.channelsStatus = 'succeeded';
                state.channelsError = null;
                state.channelsSaveError = null;
            })
            .addCase(setChannels.rejected, (state, action: any) => {
                revertEmailIntent(state, action.meta?.arg?.campaignId ?? null, action.meta?.arg?.channels);
                state.channelsStatus = 'failed';
                state.channelsError = action.payload?.error ?? 'WA_INTERNAL';
                state.channelsSaveError = action.payload?.error ?? 'WA_INTERNAL';
            })

            .addCase(getWaAvailability.pending, (state) => { state.availabilityStatus = 'loading'; })
            .addCase(getWaAvailability.fulfilled, (state, action: any) => {
                state.availabilityStatus = ok(action.payload) ? 'succeeded' : 'failed';
                state.waAvailability = action.payload?.Data ?? null;
            })
            .addCase(getWaAvailability.rejected, (state) => {
                state.availabilityStatus = 'failed';
                // A network failure is NOT "available". Absent availability renders nothing —
                // the same silence as Enabled=false (contract §6).
                state.waAvailability = null;
            })

            .addCase(getWaTemplates.pending, (state) => { state.templatesStatus = 'loading'; })
            .addCase(getWaTemplates.fulfilled, (state, action: any) => {
                state.templatesStatus = ok(action.payload) ? 'succeeded' : 'failed';
                state.waTemplates = Array.isArray(action.payload?.Data) ? action.payload.Data : [];
                // Covers the order "seed first, then templates land" — the usual one on the
                // mapping screen, where `seedWaFromUrl` runs on mount (SmartSendScreen:405) and
                // `getWaTemplates` is dispatched from a separate effect (:349).
                // Guarded on the count so a live leg's `PlaceholderCount`, already applied by
                // `getWaMapping.fulfilled`, is never overwritten by the template list.
                if (state.whatsAppTemplateId && !state.waPlaceholderCount) {
                    applyTemplateShape(state, state.whatsAppTemplateId);
                }
            })
            .addCase(getWaTemplates.rejected, (state) => { state.templatesStatus = 'failed'; })

            .addCase(getWaMapping.pending, (state, action: any) => {
                if (forOtherUnit(state, action)) return;
                state.mappingStatus = 'loading';
                // Cleared on the way OUT, so a retry that succeeds does not leave the previous
                // refusal standing next to a screen that now works.
                state.mappingError = null;
                // Cleared the moment a DIFFERENT campaign is asked for, not when its answer
                // lands: the window in between is exactly where a stale leg would be acted on.
                if (state.mappingCampaignId !== argCampaignId(action.meta?.arg)) {
                    state.mappingCampaignId = null;
                    state.waMapping = null;
                }
            })
            .addCase(getWaMapping.fulfilled, (state, action: any) => {
                // 09-channel-units — AN ANSWER ABOUT A UNIT THIS PAGE NO LONGER CARRIES IS DROPPED.
                // Every read is a unit's own (channel 3); one that lands after its page was left, or
                // after the page moved to another unit, would write another unit's server state —
                // HasLeg = 1 reads as "WhatsApp is on" on the picker — into whatever is on screen now.
                if (forOtherUnit(state, action)) return;
                if (!ok(action.payload)) {
                    state.mappingStatus = 'failed';
                    // The §4 code, never the raw server string — `waErrorText` gates it on
                    // `i18n.exists` and falls back to `WA_INTERNAL`, so an unknown code reaches
                    // the screen as a sentence rather than as a key.
                    state.mappingError = action.payload?.Message ?? 'WA_INTERNAL';
                    return;
                }
                const d: WaMappingResult | null = action.payload?.Data ?? null;
                state.mappingStatus = 'succeeded';
                state.waMapping = d;
                state.mappingCampaignId = argCampaignId(action.meta?.arg);
                if (!d) return;
                // SERVER WINS (§6 "server > URL > slice"): the unit's own template, number and map
                // replace whatever the URL seed put in the slice.
                // 09-channel-units — AND THE E-MAIL SWITCH IS NOT READ FROM HERE ANY MORE. Every read
                // is a unit's own (channel 3), and a unit's GetMapping always answers SuppressEmail = 1
                // because a container has no e-mail — true of the unit, false of a "both" page. The
                // switch is written by `setWaUnit` from the page's own units.
                if (d.HasLeg) {
                    state.withWhatsApp = true;
                    state.whatsAppTemplateId = d.WhatsAppTemplateID ?? state.whatsAppTemplateId;
                    state.whatsAppFromNumber = d.FromNumber ?? state.whatsAppFromNumber;
                    // 🔴 `?? state.waPlaceholderCount`, NOT `?? 0`. On channel 3 the container has no
                    // count until the first mapping save, and a hard 0 here WIPES the count the
                    // template picker already stored — the mapping tab then renders zero variable
                    // rows and declares itself fully mapped. When the server knows, it still wins.
                    state.waPlaceholderCount = d.PlaceholderCount ?? state.waPlaceholderCount;
                    state.waTemplateCard = d.Template
                        ? {
                            name: d.Template.FriendlyTemplateName || d.Template.TemplateName,
                            language: d.Template.Language,
                            variables: d.PlaceholderCount ?? 0,
                        }
                        : state.waTemplateCard;
                    state.waVariableMap = mapFromServer(d.Map);
                    state.waSavedMapping = {
                        campaignId: state.campaignId ?? 0,
                        templateId: d.WhatsAppTemplateID ?? null,
                        mappedCount: countMapped(mapFromServer(d.Map), d.PlaceholderCount ?? 0),
                        placeholderCount: d.PlaceholderCount ?? 0,
                    };
                }
                // Nothing that just loaded is unsaved. Same signal, same meaning as
                // SmartSendScreen's `setDirty(false)` on a fresh `tokens` array.
                state.waDirty = false;
                // §6: 'blocked' comes from HasCellIdentity on the GET/POST answer — never from an
                // SP code. It does not block the send; the e-mail goes and WhatsApp is skipped.
                state.waSaveState = d.HasLeg && d.HasCellIdentity === false ? 'blocked' : 'idle';
            })
            .addCase(getWaMapping.rejected, (state, action: any) => {
                if (forOtherUnit(state, action)) return;
                state.mappingStatus = 'failed';
                // A dropped request carries no envelope at all. `WA_INTERNAL` is the honest
                // answer, and it is the same one every other transport failure in this slice
                // reports.
                state.mappingError = 'WA_INTERNAL';
            })

            .addCase(setWaMapping.fulfilled, (state, action: any) => {
                if (forOtherUnit(state, action)) return;
                const p = action.payload;
                if (!ok(p)) {
                    state.waSaveState = 'error';
                    state.lastSaveError = p?.Message ?? null;
                    return;
                }
                const d: any = p?.Data ?? {};
                state.waSaveState = d.HasCellIdentity === false ? 'blocked' : 'saved';
                state.lastSaveError = null;
                state.waDirty = false;
                // The marker is cleared once a save has carried it, so no later save re-asserts a
                // value the operator has not touched since — the same discipline `waDirty` gets one
                // line up. (09-channel-units: every save from the current screen is a unit's, on
                // channel 3, where the server ignores SuppressEmail; nothing sets the marker there.)
                state.suppressEmailDirty = false;
                if (state.waMapping) {
                    state.waMapping.HasLeg = true;
                    // Mirrored so the screen does not read a stale `false` from the last GET
                    // until the next one lands — exactly why `HasHeader` is written here too.
                    state.waMapping.SuppressEmail = state.suppressEmail;
                    // A successful POST created or refreshed the header — HasHeader is the field
                    // every state branch reads, so it must not lag behind until the next GET.
                    state.waMapping.HasHeader = d.WhatsAppCampaignID != null;
                    state.waMapping.WhatsAppCampaignID = d.WhatsAppCampaignID ?? state.waMapping.WhatsAppCampaignID;
                    state.waMapping.WaCampaignStatus = d.WaCampaignStatus ?? state.waMapping.WaCampaignStatus;
                    state.waMapping.HasCellIdentity = d.HasCellIdentity ?? state.waMapping.HasCellIdentity;
                }
                state.waSavedMapping = {
                    campaignId: state.campaignId ?? 0,
                    templateId: state.whatsAppTemplateId,
                    mappedCount: countMapped(state.waVariableMap, state.waPlaceholderCount),
                    placeholderCount: state.waPlaceholderCount,
                };
            })
            .addCase(setWaMapping.rejected, (state, action: any) => {
                if (forOtherUnit(state, action)) return;
                state.waSaveState = 'error';
                // REPLACED, not left alone. The indicator renders `error.<lastSaveError>`, so
                // leaving the previous failure's code in place made a transport drop report
                // whatever the last SERVER refusal happened to be — e.g. "הווטסאפ כבר נשלח" on a
                // campaign that had never sent.
                state.lastSaveError = 'WA_INTERNAL';
            })

            .addCase(deleteWaMapping.rejected, (state) => {
                // Without this the dialog closed on a removal that never happened and the
                // operator navigated on believing the leg was gone.
                state.waSaveState = 'error';
                state.lastSaveError = 'WA_INTERNAL';
            })
            .addCase(deleteWaMapping.fulfilled, (state, action: any) => {
                if (!ok(action.payload)) {
                    state.waSaveState = 'error';
                    state.lastSaveError = action.payload?.Message ?? null;
                    return;
                }
                // The leg is gone: back to a clean e-mail-only campaign. Keeping the id/card
                // would re-render the WhatsApp tab over a campaign that no longer has one.
                const keepId = state.campaignId;
                Object.assign(state, initialState, {
                    campaignId: keepId,
                    mappingCampaignId: state.mappingCampaignId,
                    waAvailability: state.waAvailability,
                    availabilityStatus: state.availabilityStatus,
                    waTemplates: state.waTemplates,
                    templatesStatus: state.templatesStatus,
                    mappingStatus: 'succeeded' as LoadStatus,
                    waMapping: null,
                });
            })

            .addCase(getWaSummary.pending, (state, action: any) => {
                if (forOtherUnit(state, action)) return;
                state.summaryStatus = 'loading';
                state.summaryError = null;
            })
            .addCase(getWaSummary.fulfilled, (state, action: any) => {
                if (forOtherUnit(state, action)) return;
                if (!ok(action.payload)) {
                    state.summaryStatus = 'failed';
                    // The §4 `Message` (WA_*), so the dialog can render t('error.<Message>').
                    // The raw server string is never shown — SendSummaryDialog does not know 409.
                    state.summaryError = action.payload?.Message ?? null;
                    state.waSummary = null;
                    return;
                }
                state.summaryStatus = 'succeeded';
                state.summaryError = null;
                state.waSummary = action.payload?.Data ?? null;
            })
            .addCase(getWaSummary.rejected, (state, action: any) => {
                if (forOtherUnit(state, action)) return;
                state.summaryStatus = 'failed';
                state.waSummary = null;
            })

            .addCase(getWaPreview.pending, (state, action: any) => {
                state.previewStatus = 'loading';
                state.previewError = null;
                state.previewOffsetInFlight = action.meta?.arg?.offset ?? 0;
                state.previewReqId = action.meta?.requestId ?? null;
            })
            .addCase(getWaPreview.fulfilled, (state, action: any) => {
                // A late answer from a superseded request must change NOTHING — not the row, not
                // the marker, not the status. See `previewReqId`.
                if (action.meta?.requestId !== state.previewReqId) return;
                state.previewOffsetInFlight = null;
                state.previewReqId = null;
                if (!ok(action.payload)) {
                    const code: string | null = action.payload?.Message ?? null;
                    state.previewStatus = 'failed';
                    state.previewError = code ?? 'WA_INTERNAL';

                    // 🔴 v1.21 §4/§5 — THE TWO 406 CODES. `406 NO_CANDIDATES` (no candidate rows
                    // at all) and `406 NO_ROWS_AT_OFFSET` (the stepper is past the end) are the
                    // owner's adoption of the LIVE precedent in the sibling stepper
                    // (`SmartSendPreviewLogic.cs:416-431`), and they are EMPTY STATES, not
                    // failures: the request was understood and there is nothing to show.
                    //
                    // Two things follow, and neither is cosmetic:
                    //  (1) THE STALE ROW MUST GO. Leaving the previous sample on screen under a
                    //      message that says there is none is the C14 defect wearing a new hat,
                    //      on the surface that decides what a charged send will say.
                    //  (2) `NO_ROWS_AT_OFFSET` means the candidate set SHRANK under a stepper
                    //      that had already moved — duplicates or opt-outs changed between the
                    //      mapping save and the click. Offset 0 is the only position guaranteed
                    //      to exist while any candidate remains, so the walk restarts there.
                    //
                    // ⚠️ The `!== 0` guard is what keeps this terminating. The phone preview's
                    // fetch effect DEPENDS on `waPreview.offset`, so an unconditional write would
                    // re-fire the request on every answer — exactly the loop C7 was about. With
                    // the guard, offset 0 answering NO_ROWS_AT_OFFSET writes nothing, the state
                    // reference is unchanged, and the effect does not re-run.
                    // Every write is guarded so the branch is a genuine NO-OP once the preview is
                    // already empty: Immer then leaves `waPreview` at the same reference, nothing
                    // downstream re-computes, and the reducer cannot chase its own tail.
                    if (isPreviewEmptyCode(code)) {
                        if (state.waPreview.row !== null) state.waPreview.row = null;
                        if (Object.keys(state.waPreview.values).length > 0) state.waPreview.values = {};
                        // There is no row, so there is no offset it belongs to. Leaving a stale
                        // `rowOffset` would let the send gate pass on a row that is gone.
                        if (state.waPreview.rowOffset !== null) state.waPreview.rowOffset = null;
                        if (code === 'NO_ROWS_AT_OFFSET' && state.waPreview.offset !== 0) {
                            state.waPreview.offset = 0;
                        }
                    }
                    return;
                }
                const d: WaPreviewResult | null = action.payload?.Data ?? null;
                state.previewStatus = 'succeeded';
                state.previewError = null;
                state.waPreview.row = d;
                state.waPreview.values = d?.Values ?? {};
                // Stamped from the REQUEST, which is the only place the offset exists.
                state.waPreview.rowOffset = action.meta?.arg?.offset ?? 0;
                // 🔴 `waPreview.offset` IS NOT WRITTEN FROM THE RESPONSE, and specifically not
                // from `RowOrdinal`. The two are different numbers: `offset` is the position in
                // the shuffled candidate set (`_Preview_Stage`: `rn = ROW_NUMBER() OVER (ORDER BY
                // CHECKSUM(@prm_Seed, c.RowID), c.RowID)`, matched against `@prm_Offset + 1`),
                // while `RowOrdinal` is the row's position in the DATA SOURCE
                // (`DataSourceRows.RowOrdinal`, unique per version). They coincide by accident.
                //
                // And the write did not merely mislabel: `WhatsAppPhonePreview`'s fetch effect
                // DEPENDS on `preview.offset`, so writing it here re-fired the request, whose
                // answer wrote it again — a loop that walked the offset up through source-row
                // ordinals until the server answered `400 DATA_INCORRECT` for `offset > 4`. The
                // stepper meanwhile printed "row 138 of 5", with "next" permanently disabled.
                // The offset is CLIENT-OWNED: `setWaPreviewOffset` is its only writer.
            })
            .addCase(getWaPreview.rejected, (state, action: any) => {
                if (action.meta?.requestId !== state.previewReqId) return;
                state.previewOffsetInFlight = null;
                state.previewReqId = null;
                state.previewStatus = 'failed';
                state.previewError = 'WA_INTERNAL';
            })

            .addCase(sendWaTest.pending, (state) => {
                // Cleared on the way OUT, not on the way back: without this a transport failure
                // leaves the PREVIOUS run's per-number results on screen and the operator reads
                // them as this run's.
                state.waTestResult = null;
                state.waTestError = null;
            })
            .addCase(sendWaTest.fulfilled, (state, action: any) => {
                if (!ok(action.payload)) {
                    // A route-level refusal: 400/422 DATA_INCORRECT, 404 WA_NO_LEG /
                    // CAMPAIGN_NOT_FOUND, 409 WA_ALREADY_SENT, or any MapWaReturnCode arm. None
                    // of them carries `Data`, so there is nothing to list — but there IS a
                    // `Message`, and it has an `error.*` text in all three languages.
                    state.waTestResult = null;
                    state.waTestError = action.payload?.Message ?? 'WA_INTERNAL';
                    return;
                }
                state.waTestResult = Array.isArray(action.payload?.Data) ? action.payload.Data : [];
                state.waTestError = null;
            })
            .addCase(sendWaTest.rejected, (state) => {
                state.waTestResult = null;
                state.waTestError = 'WA_INTERNAL';
            })

            // 09-channel-units — THE WHATSAPP UNIT'S OWN SEND: "WhatsApp only", and the retry of the
            // unit after a "both" send. It had no reducer at all, so its answer never reached the
            // receipt. `PUT WhatsAppOnly/Send` puts the outcome in `Data` whenever its send SP ran
            // (success and mapped refusal alike); a refusal that answers BEFORE any send (WaGate,
            // the money gate, -16…) has no `Data` and is recorded as "not sent" with its reason — the
            // retry has no result card of its own, so this line is the only place that reason can
            // appear. A charge that was never refunded is told apart by the receipt from the unit's
            // re-read state (`chargedNoSend`). A lost response on a charged send is
            // INTERNAL_AFTER_DISPATCH, never "not sent" (`.rejected` below).
            .addCase(sendWhatsAppOnly.fulfilled, (state, action: any) => {
                if (forOtherUnit(state, action)) return;
                const d = action.payload?.Data ?? null;
                if (d) { state.waSendOutcome = d; return; }
                state.waSendOutcome = ok(action.payload) ? null : {
                    State: 'Failed',
                    Code: -1,
                    Message: action.payload?.Message ?? 'WA_INTERNAL',
                    Queued: 0,
                    WhatsAppCampaignID: state.waUnitId,
                };
            })
            .addCase(sendWhatsAppOnly.rejected, (state, action: any) => {
                if (forOtherUnit(state, action)) return;
                state.waSendOutcome = {
                    State: 'Failed',
                    Reason: 'INTERNAL_AFTER_DISPATCH',
                    Code: -1,
                    Message: 'WA_INTERNAL',
                    Queued: 0,
                    WhatsAppCampaignID: state.waUnitId,
                };
            })

            // ══ 08-mapping-memory ════════════════════════════════════════════════════════
            // 🔴 EVERY FAILURE ENDS IN `[]`, AND `[]` IS THE SCREEN AS IT WAS BEFORE THIS FEATURE.
            // The route answers 500 until the SQL is deployed, and a convenience read must never
            // be the reason a paid send's mapping screen misbehaves: no banner, no retry, no
            // status anybody gates on — the tab simply has no memory rows to offer.
            .addCase(getWaMemorySuggestions.pending, (state, action: any) => {
                state.waMemorySuggestionsReqId = action.meta?.requestId ?? null;
                state.waMemorySuggestionsStatus = 'loading';
                // Rows that answer a DIFFERENT key go on the way OUT, not when the new answer
                // lands. The tab would not show them anyway (it compares keys); this makes the
                // state itself unable to say anything about the new key while it is on the wire.
                const key = action.meta?.arg ? waMemorySuggestionsKey(action.meta.arg) : null;
                if (state.waMemorySuggestionsKey !== key) {
                    state.waMemorySuggestions = [];
                    state.waMemorySuggestionsKey = null;
                }
            })
            .addCase(getWaMemorySuggestions.fulfilled, (state, action: any) => {
                // A late answer from a superseded request changes NOTHING — `previewReqId` rule.
                if (action.meta?.requestId !== state.waMemorySuggestionsReqId) return;
                state.waMemorySuggestionsReqId = null;
                // `PulseemResponse`: a refusal sits in the BODY of an HTTP 200, so `ok()` is the
                // real failure path and `rejected` only the transport one.
                if (!ok(action.payload)) {
                    state.waMemorySuggestionsStatus = 'failed';
                    state.waMemorySuggestions = [];
                    state.waMemorySuggestionsKey = null;
                    return;
                }
                state.waMemorySuggestions = Array.isArray(action.payload?.Data) ? action.payload.Data : [];
                state.waMemorySuggestionsKey = waMemorySuggestionsKey(action.meta.arg);
                state.waMemorySuggestionsStatus = 'succeeded';
            })
            .addCase(getWaMemorySuggestions.rejected, (state, action: any) => {
                if (action.meta?.requestId !== state.waMemorySuggestionsReqId) return;
                state.waMemorySuggestionsReqId = null;
                state.waMemorySuggestionsStatus = 'failed';
                state.waMemorySuggestions = [];
                state.waMemorySuggestionsKey = null;
            });
    },
});

export const {
    setWithWhatsApp, setSuppressEmail, resetWaIntentForCampaign, resetWaForPicker, selectWaTemplate, setWaFromNumber, setWaVariable,
    markWaEdited, setWaSaveState, setWaPreviewOffset, clearWaSendOutcome, clearWaTestResult,
    clearWaSummaryError, clearWaUsesUnitErrors, seedWaFromUrl, setWaUnit,
} = smartSendWhatsAppSlice.actions;

export default smartSendWhatsAppSlice.reducer;
